import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';

const POLL_INTERVAL_MS = 30_000;
const apiUrl = process.env.REACT_APP_API_URL ?? '';

interface UseUnreadMessageCountResult {
  count: number;
  messages: UnreadMessageSummary[];
  refresh: () => void;
  markRead: (id: string) => Promise<void>;
}

export interface UnreadMessageSummary {
  id: string;
  from: string;
  subject: string;
  snippet: string;
  dateTime: string;
  notificationType?: string | null;
  metadata?: {
    machineRepairRequestId?: string;
    monthlyPlanId?: string;
    salesYear?: number;
    salesMonth?: number;
    parentDocument?: string;
    rnWorkOrderId?: string;
    rnWorkOrderNumber?: string | null;
    subprocessId?: string;
    machineId?: string;
    event?: string;
  } | null;
}

// ─── Module-level singleton: ONE socket, ONE poller, shared across every ───
// ─── mounted hook instance (keep-alive tabs can mount this many times). ────

let _count = 0;
let _messages: UnreadMessageSummary[] = [];
const _subscribers = new Set<(count: number) => void>();
let _socket: Socket | null = null;
let _intervalId: ReturnType<typeof setInterval> | null = null;
const _inflight = { promise: null as Promise<void> | null };
let _visibilityHandlerRegistered = false;

// Other screens listen to server events on this same single socket (e.g. live plan refresh).
const _eventListeners = new Map<string, Set<(payload: any) => void>>();

export const onSocketEvent = (event: string, handler: (payload: any) => void) => {
  if (!_eventListeners.has(event)) _eventListeners.set(event, new Set());
  _eventListeners.get(event)!.add(handler);
  return () => { _eventListeners.get(event)?.delete(handler); };
};

const notifySubscribers = () => {
  _subscribers.forEach((cb) => cb(_count));
};

const fetchCount = (): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve();

  if (_inflight.promise) return _inflight.promise;

  _inflight.promise = axios
    .get<{ count: number; messages?: UnreadMessageSummary[] }>(`${apiUrl}/api/communication/inbox/unread-count`, {
      headers: { 'x-auth-token': token },
      // Background polling is not user activity — it must not postpone the auto-logout.
      skipInactivityReset: true,
    } as any)
    .then((res) => {
      // A reply that belongs to a previous login (token changed meanwhile) is dropped.
      if (localStorage.getItem('token') !== token) return;
      _count = res.data.count ?? 0;
      _messages = res.data.messages ?? [];
      notifySubscribers();
    })
    .catch(() => {})
    .finally(() => {
      _inflight.promise = null;
    });

  return _inflight.promise;
};

const startPolling = () => {
  if (_intervalId) clearInterval(_intervalId);
  _intervalId = setInterval(fetchCount, POLL_INTERVAL_MS);
};

const stopPolling = () => {
  if (_intervalId) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
};

const handleDecrement = () => {
  fetchCount();
};

const handleVisibilityChange = () => {
  if (document.hidden) {
    stopPolling();
  } else {
    fetchCount();
    startPolling();
  }
};

const startShared = () => {
  // Poll immediately, then on interval
  fetchCount();
  startPolling();

  if (!_visibilityHandlerRegistered) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('decrementCommunicationNotification', handleDecrement);
    _visibilityHandlerRegistered = true;
  }

  // Socket.io: real-time badge update
  const token = localStorage.getItem('token');
  if (token && !_socket) {
    // Connect to same origin — CRA proxy forwards in dev; same origin in prod
    // Token is read on every (re)connect: after a password change the server drops the
    // socket and the stored token is already the new one, so the reconnect succeeds.
    const socket = io(undefined as any, {
      auth: (cb) => cb({ token: localStorage.getItem('token') || '' }),
      reconnectionAttempts: 5,
    });
    _socket = socket;

    socket.onAny((event: string, payload: unknown) => {
      _eventListeners.get(event)?.forEach((handler) => handler(payload));
    });

    socket.on('newMessage', () => {
      fetchCount();
      window.dispatchEvent(new CustomEvent('communicationNewMessage'));
    });

    // The server drops the socket when the token expires or the session is revoked; socket.io
    // does NOT auto-reconnect after a server-side disconnect, so reconnect by hand (with the
    // current token) as long as this browser is still logged in.
    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect' && localStorage.getItem('token') && _socket === socket) {
        setTimeout(() => { if (_socket === socket) socket.connect(); }, 1000);
      }
    });

    socket.on('connect_error', () => {
      // Silent catch — polling remains as fallback
    });
  }
};

const stopShared = () => {
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('decrementCommunicationNotification', handleDecrement);
  _visibilityHandlerRegistered = false;

  if (_socket) {
    _socket.disconnect();
    _socket = null;
  }
  // Nothing of the previous account survives a logout: the next login starts from zero.
  _count = 0;
  _messages = [];
  _inflight.promise = null;
  notifySubscribers();
};

const subscribe = (cb: (count: number) => void) => {
  const isFirst = _subscribers.size === 0;
  _subscribers.add(cb);
  if (isFirst) {
    startShared();
  } else {
    // Not first — deliver current value immediately for instant paint.
    cb(_count);
  }
  return () => {
    _subscribers.delete(cb);
    if (_subscribers.size === 0) {
      stopShared();
    }
  };
};

const useUnreadMessageCount = (): UseUnreadMessageCountResult => {
  const [count, setCount] = useState(_count);
  const [messages, setMessages] = useState<UnreadMessageSummary[]>(_messages);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const handleUpdate = (next: number) => {
      if (mountedRef.current) {
        setCount(next);
        setMessages(_messages);
      }
    };
    const unsubscribe = subscribe(handleUpdate);

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, []);

  const refresh = useCallback(() => {
    fetchCount();
  }, []);

  const markRead = useCallback(async (id: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    await axios.patch(`${apiUrl}/api/communication/inbox/${id}/read`, {}, {
      headers: { 'x-auth-token': token },
    });
    _messages = _messages.filter((message) => message.id !== id);
    _count = Math.max(0, _count - 1);
    notifySubscribers();
  }, []);

  return { count, messages, refresh, markRead };
};

export default useUnreadMessageCount;
